import React, { FC, useEffect } from 'react';

import { DataGroupListResponse } from '@ariksa/abac/api';
import { HStack, Stack, Text } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { customTheme } from 'theme';

import {
  DetailsIdentifier,
  renderStackedCloudNameAndIcon,
  StackedHeader,
} from 'components/DataDisplay';
import { IconTypes } from 'components/Icons';
import { Drawer } from 'components/Overlay';
import { ActiveResourceProvider } from 'containers/ActiveCloudResource/hooks/context/ActiveResourceProvider';
import { DataGroupOverviewTables } from 'containers/Jit/DataGroups/Components/Drawer/DataGroupOverviewTables';
import { OverlappingApprovers } from 'containers/Jit/DataGroups/Components/Drawer/OverlappingApprovers';
import {
  formatAutoApprovalDuration,
  getDataGroupApproverEmails,
} from 'containers/Jit/DataGroups/Components/Drawer/utils';
import { selectJit } from 'containers/Jit/selectors';
import { actions as jitActions } from 'containers/Jit/slice';

import { selectDataGroups } from '../../selectors';
import { actions } from '../../slice';

interface IDataGroupDrawer {
  isOpen: boolean;
  onClose: () => void;
  row: DataGroupListResponse;
}

export const DataGroupDrawer: FC<IDataGroupDrawer> = props => {
  const { isOpen, onClose, row } = props;
  const dispatch = useDispatch();
  const { dataGroup } = useSelector(selectDataGroups);
  const { approvers, autoApprovalConfiguration } = useSelector(selectJit);

  useEffect(() => {
    if (row?.uuid) {
      dispatch(actions.getDataGroup({ q: { dataGroupId: row.uuid } }));
      dispatch(actions.getAuthorizedGroups({ q: { dataGroupId: row.uuid } }));
    }
  }, [dispatch, row?.uuid]);

  // Load approvers and the auto-approval limit shown in the drawer details.
  useEffect(() => {
    if (!row?.uuid || !row?.account_id) return;
    const q = { accountId: row.account_id, dataGroupId: row.uuid };
    //dispatch(jitActions.getApprovers({ q }));
    //dispatch(jitActions.getAutoApprovalConfiguration({ q }));
  }, [dispatch, row?.uuid, row?.account_id]);

  // Group's own value first, else the account-wide one it inherits.
  const autoApprovalSeconds =
    autoApprovalConfiguration.data?.configuration?.max_duration_seconds ??
    autoApprovalConfiguration.data?.effective_configuration
      ?.max_duration_seconds;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      header={
        <StackedHeader
          upper={row?.name}
          lower="Data Group"
          type={IconTypes.DataGroup}
          style={{ p: 1.5 }}
          color={customTheme.colors.blue['300']}
          useCustomColor
        />
      }
      body={
        <Stack spacing={10} h="full">
          <HStack align="flex-start">
            <DetailsIdentifier
              label="Description"
              value={
                <Text noOfLines={2} whiteSpace="wrap">
                  {row.description}
                </Text>
              }
              w="60%"
              isInline={false}
              spacing={0}
            />
            <Stack spacing={3}>
              <DetailsIdentifier
                label="Cloud"
                value={renderStackedCloudNameAndIcon('snowflake', true)}
              />
              <DetailsIdentifier
                label="Approvers"
                align="center"
                isLoading={approvers.isLoading}
                value={
                  <OverlappingApprovers
                    emails={getDataGroupApproverEmails(approvers.data)}
                  />
                }
              />
              <DetailsIdentifier
                label="Auto-Approval Duration"
                isLoading={autoApprovalConfiguration.isLoading}
                value={formatAutoApprovalDuration(autoApprovalSeconds)}
              />
            </Stack>
          </HStack>
          <ActiveResourceProvider>
            <DataGroupOverviewTables
              dataGroup={dataGroup.data}
              isLoading={dataGroup.isLoading}
            />
          </ActiveResourceProvider>
        </Stack>
      }
      styles={{ content: { maxW: '1200px' } }}
    />
  );
};
